import { getAddress, getContract, getLogs } from '@/utils/ether';
import { useWallet } from '@/hooks/wallet';
import { useRecoilValue } from 'recoil';
import { appConfigAtom } from '@/state/app';
import { getNetworkByChainId, getVocabByToken } from '@/utils/utils';
import { walletAtom } from '@/state/wallet';
import { BigNumber, ethers } from 'ethers';
import { LogClaim, LogWithdraw } from '@/utils/contracts/types';
import { Contract, ContractInstance } from '@/constants/types';

export const useContracts = () => {
    const { provider, signer } = useWallet();
    const appConfig = useRecoilValue(appConfigAtom);
    const walletState = useRecoilValue(walletAtom);
    const tokenVocab = getVocabByToken(appConfig.token);
    const network = getNetworkByChainId(walletState.chainId)?._id;
    const baseInfo = { token: appConfig.token, network, provider: signer } as ContractInstance;

    const ClayMain = () => getContract({ ...baseInfo, contractName: 'ClayMain' });
    const ClayExchangeV1 = () => getContract({ ...baseInfo, contractName: 'ClayExchangeV1' });

    // Token Methods
    const getTokenBalance = async (tokenName: Contract) => {
        if (tokenVocab.isNative && tokenName == 'Token') {
            return provider.getBalance(walletState.selectedAddress!);
        } else {
            const tokenContract = await getContract({ ...baseInfo, contractName: tokenName });
            if (tokenContract) {
                return tokenContract.balanceOf(walletState.selectedAddress!);
            } else {
                return BigNumber.from(0);
            }
        }
    };
    const getTokenAllowance = async (tokenName: Contract, contractName: Contract) => {
        if (tokenVocab.isNative && tokenName == 'Token') {
            return ethers.constants.MaxUint256;
        } else {
            const tokenContract = await getContract({ ...baseInfo, contractName: tokenName });
            const address = getAddress({ ...baseInfo, contractName });
            if (address && tokenContract) {
                return tokenContract.allowance(walletState.selectedAddress!, address);
            } else {
                return BigNumber.from(0);
            }
        }
    };

    // ClayMain Methods
    const getExchangeRate = async () => (await ClayMain())?.getExchangeRate();
    const getFees = async () => (await ClayMain())?.fees();
    const getFlashExitLimit = async () => {
        const contract = appConfig.token === 'CSMATIC_POLYGON' ? await ClayExchangeV1() : await ClayMain();
        if (tokenVocab.isFlashExitEnabled) {
            return contract?.getLiquidityCsToken();
        } else {
            return BigNumber.from(0);
        }
    };
    const getUserOrders = async (page: number) => {
        if (appConfig.token === 'CSETH' || appConfig.token === 'CSMATIC_POLYGON') {
            return (await ClayMain())?.getUserOrders(walletState.selectedAddress, page);
        } else {
            return null;
        }
    };
    const getLogsClayMain = async <T>(eventName: string): Promise<T[]> => {
        const contract = await ClayMain();
        return await getLogs(eventName, [walletState.selectedAddress], contract);
    };

    // MATIC Ethereum/Goerli
    const getEpoch = async () => {
        if (appConfig.token != 'CSMATIC') {
            return [BigNumber.from(0), BigNumber.from(0)];
        } else {
            return (await ClayMain())?.getEpoch();
        }
    };
    const getStandardUnstakeLimit = async () => {
        const contract = await ClayMain();
        if (appConfig.token != 'CSMATIC' || !contract) {
            return ethers.constants.MaxUint256;
        } else {
            return contract.getMaxWithdrawAmountCs();
        }
    };
    const getWithdrawLogs = async () => await getLogsClayMain<LogWithdraw>('LogWithdraw');
    const getClaimLogs = async () => await getLogsClayMain<LogClaim>('LogClaim');

    return {
        getTokenBalance,
        getTokenAllowance,
        getExchangeRate,
        getFees,
        getFlashExitLimit,
        getUserOrders,
        getEpoch,
        getStandardUnstakeLimit,
        getWithdrawLogs,
        getClaimLogs,
    };
};
